<template>
  <div class="p-6 space-y-6">
    <div>
      <div class="flex items-center gap-2 mb-3">
        <span class="px-2 py-1 rounded-lg text-xs font-medium" :class="platformClasses">
          <i :class="platformIcon" class="mr-1" />{{ finding.platform }}
        </span>
        <span class="px-2 py-1 rounded-lg text-xs font-medium" :class="intentMeta(finding.intent).classes">
          {{ intentMeta(finding.intent).label }}
        </span>
        <span class="px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 capitalize">
          {{ finding.status }}
        </span>
        <span class="text-sm text-gray-500" v-tooltip="'AI relevance score (0–100)'">
          <i class="far fa-signal mr-1" />{{ finding.relevance_score }}/100
        </span>
      </div>
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {{ finding.title || finding.url }}
      </h2>
    </div>

    <div>
      <h3 class="text-xs font-semibold text-gray-500 uppercase mb-2">
        Conversation
      </h3>
      <blockquote class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
        {{ finding.snippet || 'No excerpt captured for this conversation.' }}
      </blockquote>
    </div>

    <div v-if="finding.why_relevant">
      <h3 class="text-xs font-semibold text-gray-500 uppercase mb-2">
        Why it's an opportunity
      </h3>
      <p class="text-sm text-gray-700 dark:text-gray-300">
        <i class="far fa-lightbulb text-amber-500 mr-1" />{{ finding.why_relevant }}
      </p>
    </div>

    <dl class="grid grid-cols-2 gap-4 text-sm">
      <div v-if="finding.topic">
        <dt class="text-xs text-gray-500 uppercase">
          Search topic
        </dt>
        <dd class="text-gray-900 dark:text-gray-100 mt-0.5">
          {{ finding.topic }}
        </dd>
      </div>
      <div v-if="finding.conversation_posted_at">
        <dt class="text-xs text-gray-500 uppercase">
          Posted (approx.)
        </dt>
        <dd class="text-gray-900 dark:text-gray-100 mt-0.5">
          {{ finding.conversation_posted_at }}
        </dd>
      </div>
      <div>
        <dt class="text-xs text-gray-500 uppercase">
          Found
        </dt>
        <dd class="text-gray-900 dark:text-gray-100 mt-0.5">
          {{ formatDate(finding.created_at) }}
        </dd>
      </div>
      <div class="col-span-2">
        <dt class="text-xs text-gray-500 uppercase">
          URL
        </dt>
        <dd class="mt-0.5 break-all">
          <a :href="finding.url" target="_blank" rel="noopener" class="text-primary-600 dark:text-primary-400 hover:underline">
            {{ finding.url }}
          </a>
        </dd>
      </div>
    </dl>

    <div class="flex items-center justify-between pt-4 border-t dark:border-gray-700">
      <a :href="finding.url" target="_blank" rel="noopener" class="btn btn-primary btn-sm">
        <i class="far fa-external-link-alt mr-1" />
        Open conversation
      </a>
      <div class="flex gap-2">
        <button
          v-if="finding.status !== 'reviewed'"
          class="btn btn-secondary btn-sm"
          @click="$emit('set-status', finding, 'reviewed')">
          <i class="far fa-check mr-1" />Mark reviewed
        </button>
        <button
          v-if="finding.status !== 'dismissed'"
          class="btn btn-secondary btn-sm text-gray-500"
          @click="$emit('set-status', finding, 'dismissed')">
          <i class="far fa-times mr-1" />Dismiss
        </button>
        <button
          v-if="finding.status !== 'new'"
          class="btn btn-secondary btn-sm text-gray-500"
          @click="$emit('set-status', finding, 'new')">
          Restore
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import { intentMeta } from './intents'

const props = defineProps({
  finding: { type: Object, required: true }
})

defineEmits(['set-status', 'on-close-panel'])

const platformStyles = {
  reddit: { classes: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300', icon: 'fab fa-reddit-alien' },
  facebook: { classes: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300', icon: 'fab fa-facebook' },
  nextdoor: { classes: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300', icon: 'far fa-home' },
  other: { classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300', icon: 'far fa-comment' }
}

const platformClasses = computed(() => (platformStyles[props.finding.platform] || platformStyles.other).classes)
const platformIcon = computed(() => (platformStyles[props.finding.platform] || platformStyles.other).icon)

const formatDate = (iso) => new Date(iso).toLocaleString()
</script>
